import { ModalTemplate } from "@mlpa/core-ui-core-open_ocean_ui/Templates/Modals/ModalTemplate";
import translations from "translations";
import {
  GetModulesQuery,
  PaginatedResultType,
  useGetModulesLazyQuery,
} from "graphqlBase/modules/__generated__/getModules";
import { useDataGridOO } from "../dataGridTable";

export type Modules = PaginatedResultType<GetModulesQuery["modules"]>;

interface BackendServiceOverviewProps {
  open: boolean;
  handleClose: () => void;
}

const BackendServiceOverview: React.FC<BackendServiceOverviewProps> = ({ open, handleClose }) => {
  const [query, { data }] = useGetModulesLazyQuery({
    fetchPolicy: "cache-and-network",
  });

  const { DataGridOO, useMakeColumns, makeSeverSideFilter, apiRef } = useDataGridOO<Modules, "Module">({
    query,
    variables: {},
    tableId: "AssetOverview",
    persistance: "runTime",
  });

  const gridRows: Modules[] =
    data?.modules?.items?.map(({ id, moduleName, lastStartedModuleInstance }) => ({
      id,
      moduleName,
      lastStartedModuleInstance,
    })) || [];
  const columns = useMakeColumns(
    [
      {
        field: "service",
        headerName: translations.globals.terms.service,
        flex: 1.3,
        valueGetter: (_, row) => {
          return row?.moduleName ?? "";
        },
        remoteOrder: (sort) => ({ moduleName: sort }),
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ filterValue }) => ({
            moduleName: filterValue,
          }),
        }),
        type: "string",
      },
      {
        field: "version",
        headerName: translations.globals.terms.version,
        flex: 1,
        valueGetter: (_, row) => {
          return row?.lastStartedModuleInstance?.runtimeVersion ?? "";
        },
        remoteOrder: (sort) => ({ lastStartedModuleInstance: { runtimeVersion: sort } }),
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ filterValue }) => ({
            lastStartedModuleInstance: { runtimeVersion: filterValue },
          }),
        }),
        type: "string",
      },
      {
        field: "hash",
        headerName: translations.globals.terms.hash,
        flex: 4,
        valueGetter: (_, row) => {
          return row?.lastStartedModuleInstance?.entryAssemblyVersion ?? "";
        },
        type: "string",
      },
      {
        field: "status",
        headerName: translations.globals.terms.status,
        flex: 1.3,
        valueGetter: (_, row) => {
          return row?.lastStartedModuleInstance?.healthStatus ?? "";
        },
        type: "string",
      },
    ],
    []
  );

  return (
    <ModalTemplate
      open={open}
      onClose={handleClose}
      disableRestoreFocus
      width={800}
      title={translations.globals.headlines.backendServiceOverview}
      primaryButtonProps={{
        label: translations.globals.button.close,
        onClick: handleClose,
      }}
    >
      <DataGridOO
        disableRowSelectionOnClick
        columns={columns}
        rows={gridRows}
        apiRef={apiRef}
        checkboxSelection={false}
        showToolbar={false}
        totalCount={data?.modules?.items?.length || 0}
      />
    </ModalTemplate>
  );
};

export default BackendServiceOverview;
